import { useId } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import { Link } from "@/libraries/design/components/link/src/link";
import { ColumnLayout, wacToken } from "@/libraries/design/foundations";
import { RichTextComponent } from "@/libraries/design/utils";
import { useDarkTheme } from "@/libraries/design/utils/dark-theme-provider";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

export interface CtaProps {
    label: string;
    href: string;
    dataEventProps?: DataEventProps;
}

export type SecondaryCtaType = "link" | "button";

export interface BottomCallToActionProps {
    title: React.ReactElement<typeof RichTextComponent>;
    emphasisColor?: string;
    body?: React.ReactElement<typeof RichTextComponent>;
    button?: CtaProps;
    secondaryCta?: CtaProps;
    secondaryCtaType?: SecondaryCtaType;
}

const containerStyles = css({
    backgroundColor: wacToken("color.primary.black"),
    paddingBlock: token("space.800"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["p, h2, h2 > span, h2 > div"]: {
        color: wacToken("color.neutral.light0"),
        textAlign: "center",
        margin: 0
    }
});

const darkThemeContainerStyles = css({
    backgroundColor: "transparent"
});

const innerContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: token("space.150"),
    [media.above.sm]: {
        paddingBlock: token("space.800")
    }
});

const buttonContainerStyles = css({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: token("space.300"),
    paddingBlockStart: token("space.400")
});

// Rich-text `p` sits inside TypographyStyles, where refresh adds `p, .body-md` at lg+ with a
// direct font-size that overrides inheritance from `.card-title-sm`. Target paragraphs here so
// copy matches card-title-sm (same tokens as typography-styles `.card-title-sm` base + md).
const bodyCopyStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& p"]: {
        fontFamily: wacToken("font.charlie.display"),
        fontSize: "1.125rem",
        fontWeight: 700,
        lineHeight: "1.5rem",
        letterSpacing: "0.3px",
        color: wacToken("color.neutral.light0"),
        textAlign: "center",
        margin: 0,
        [media.above.md]: {
            fontSize: "1.25rem",
            lineHeight: "1.625rem"
        },
        // Explicit lg so WAC refresh global `p` rules do not shrink body copy again.
        [media.above.lg]: {
            fontSize: "1.25rem",
            lineHeight: "1.625rem",
            fontWeight: 700
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["& p + p"]: {
        marginBlockStart: token("space.200")
    }
});

export const BottomCallToAction = ({
    title,
    emphasisColor,
    body,
    button,
    secondaryCta,
    secondaryCtaType = "link"
}: BottomCallToActionProps) => {
    const uniqueId = useId();
    const titleId = `bottom-cta-title-${uniqueId}`;
    const buttonId = `bottom-cta-button-${uniqueId}`;
    const secondaryCtaId = `bottom-cta-secondary-${uniqueId}`;

    const hasSecondaryCta = secondaryCta?.href && secondaryCta?.label;

    const { isDarkTheme } = useDarkTheme();

    const emphasisStyles = css({
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["em"]: {
            color: emphasisColor,
            fontStyle: "unset"
        }
    });

    return (
        <div css={[containerStyles, isDarkTheme && darkThemeContainerStyles]}>
            <ColumnLayout layout="8" tabletLayout="12">
                <div css={innerContainerStyles}>
                    <h2
                        id={titleId}
                        className={
                            isDarkTheme ? "mono-font-family-heading" : "h1-lg"
                        }
                        css={[emphasisColor && emphasisStyles]}
                    >
                        {title}
                    </h2>
                    {body ? (
                        <div className="card-title-sm" css={bodyCopyStyles}>
                            {body}
                        </div>
                    ) : null}
                    {(button || hasSecondaryCta) && (
                        <div css={buttonContainerStyles}>
                            {button && (
                                <Button
                                    id={buttonId}
                                    href={button.href}
                                    responsiveSize={{
                                        mobile: "small",
                                        tablet: "small",
                                        desktop: "large",
                                        xl_desktop: "large"
                                    }}
                                    ariaLabelledby={`${buttonId} ${titleId}`}
                                    dataEventProps={button.dataEventProps}
                                >
                                    {button.label}
                                </Button>
                            )}
                            {hasSecondaryCta &&
                                (secondaryCtaType === "button" ? (
                                    <Button
                                        id={secondaryCtaId}
                                        href={secondaryCta.href}
                                        variant="secondary"
                                        colorAgainst="black"
                                        responsiveSize={{
                                            mobile: "small",
                                            tablet: "small",
                                            desktop: "large",
                                            xl_desktop: "large"
                                        }}
                                        ariaLabelledby={`${secondaryCtaId} ${titleId}`}
                                        dataEventProps={
                                            secondaryCta.dataEventProps
                                        }
                                    >
                                        {secondaryCta.label}
                                    </Button>
                                ) : (
                                    <Link
                                        id={secondaryCtaId}
                                        text={secondaryCta.label}
                                        href={secondaryCta.href}
                                        type="primary"
                                        bgColor={
                                            isDarkTheme
                                                ? undefined
                                                : "bgBlackBlue"
                                        }
                                        aria-label={secondaryCta.label}
                                        ariaLabelledby={`${secondaryCtaId} ${titleId}`}
                                        aria-labelledby={`${secondaryCtaId} ${titleId}`}
                                        dataEventProps={
                                            secondaryCta.dataEventProps
                                        }
                                    />
                                ))}
                        </div>
                    )}
                </div>
            </ColumnLayout>
        </div>
    );
};
